/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { isEqual, intersectionBy, orderBy } from 'lodash';
import Default from '../../constants/defaults';
import { WorkPackageStatuses } from '../../constants/storyStatusTypes';
import Holon from '../Buttons/holon';
import { sortList } from '../../actions/workPackageActions';
import StoryList from '../PlanWall/storyList';
import VrsFilterDropdown from '../PlanWall/vrsFilterDropdown';
import ValueFilterDropdown from '../PlanWall/valueFilterDropdown';
import SprintFilter from '../PlanWall/sprintFilter';
import StateFilter from '../PlanWall/stateFilter';
import AddColumn from '../PlanWall/addColumn';
import { filterOutAcceptedStories } from '../../selectors/workPackageSelector';

class ResultsTable extends React.Component {
  constructor(props) {
    super(props);

    this.listHeader = null;

    let tableFields;
    if (props.scopeTab) {
      tableFields = Default.tableFields.cadenceScope.fields;
    } else {
      tableFields = Default.tableFields.cadence.fields;
    }
    // TODO: Add filter functionality to targets and owners
    this.state = {
      results: props.resultList,
      sortDescending: true,
      sortField: 'rank',
      vrsFilter: {},
      fieldWidths: {},
      sprintFilter: [],
      stateFilter: [],
      tableFields,
      savedState: props.preservedState
    };
    this.activateSort = this.activateSort.bind(this);
    this.buildVrsFilter = this.buildVrsFilter.bind(this);
    this.filterByVrs = this.filterByVrs.bind(this);
    this.filterBySprint = this.filterBySprint.bind(this);
    this.selectColumns = this.selectColumns.bind(this);
    this.saveComponentState = this.saveComponentState.bind(this);
    this.calculateListDimensions = this.calculateListDimensions.bind(this);
    this.applyFilters = this.applyFilters.bind(this);
  }

  componentWillMount() {
    if (!this.props.noPreserve) this.processComponentState();
  }

  componentDidMount() {
    window.addEventListener('resize', this.calculateListDimensions);
    this.calculateListDimensions();
  }

  componentWillReceiveProps(nextProps) {
    this.setState({ results: nextProps.resultList });
  }

  componentDidUpdate() {
    if (
      !isEqual(this.props.preservedState, this.state.savedState) &&
      !this.props.noPreserve
    ) {
      this.processComponentState();
    }

    this.calculateListDimensions();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.calculateListDimensions);
  }

  processComponentState() {
    const { preservedState } = this.props;
    const { tableFields } = this.state;

    let {
      sortField,
      sortDescending,
      vrsFilter,
      showCompleted,
      sprintFilter
    } = this.state;
    const visibleFieldNames = preservedState.col
      ? atob(preservedState.col).split(',')
      : [];
    const sort = preservedState.sort
      ? atob(preservedState.sort).split(',')
      : [];
    const filters = preservedState.fil ? atob(preservedState.fil) : '{}';
    visibleFieldNames.forEach((name) => {
      const index = tableFields.findIndex(
        (field) => field.name.toLowerCase() === name.toLowerCase()
      );
      tableFields[index].visible = true;
    });
    [sortField] = sort;
    sortDescending = JSON.parse(sort[1]);
    vrsFilter = JSON.parse(filters).vrsFilter || {};
    sprintFilter = JSON.parse(filters).sprintFilter || [];
    showCompleted = preservedState.sc;
    this.setState(
      {
        sortField,
        sortDescending,
        vrsFilter,
        sprintFilter,
        showCompleted,
        tableFields,
        savedState: { ...preservedState }
      },
      () => this.filterByVrs()
    );
  }

  saveComponentState() {
    const {
      tableFields,
      sortField,
      sortDescending,
      vrsFilter,
      sprintFilter
    } = this.state;
    const visibleFields = tableFields.filter((field) => field.visible);
    const visibleFieldNames = visibleFields.reduce(
      (acc, el) => (acc.length ? acc.concat(el.name) : [el.name]),
      []
    );
    const savedState = {
      ...this.props.preservedState,
      col: btoa(visibleFieldNames),
      sort: btoa([sortField, sortDescending]),
      fil: btoa(JSON.stringify({ vrsFilter, sprintFilter }))
    };
    this.setState({ savedState });
    this.props.preserveComponentState(savedState);
  }

  activateSort(field) {
    let criteria = field;
    if (this.state.sortField === criteria) criteria = 'rank';
    const ascending =
      criteria === 'rank' || criteria === 'name' || criteria === 'description';

    this.setState(
      {
        sortField: criteria,
        sortDescending: !ascending
      },
      () => {
        const order = this.state.sortDescending ? 'desc' : 'asc';
        const sortedResults = orderBy(this.state.results, criteria, order);
        this.setState({ results: sortedResults });
        if (!this.props.noPreserve) this.saveComponentState();
      }
    );
  }

  buildVrsFilter(action, type, value) {
    const { vrsFilter } = this.state;
    const filterByVrs = vrsFilter[type] || [];
    const index = filterByVrs.findIndex((item) => item === value);
    if (action === 'add' && index < 0) {
      vrsFilter[type] = vrsFilter[type]
        ? vrsFilter[type].concat(value)
        : [value];
    } else if (action === 'remove') {
      vrsFilter[type].splice(index, 1);
    } else {
      vrsFilter[type] = [];
    }
    this.setState({ vrsFilter }, () => this.filterByVrs());
    if (!this.props.noPreserve) this.saveComponentState();
  }

  filterByVrs() {
    const { vrsFilter } = this.state;
    let results = this.props.resultList;
    const sorted = {};
    const filterKeys = Object.keys(vrsFilter);
    filterKeys.forEach((key, index) => {
      sorted[key] = vrsFilter[key].length
        ? results.filter((item) => {
            // handle unassigned filter option
            if (
              vrsFilter[key].includes('unassigned') &&
              !item.result_type.type_attributes[key]
            ) {
              return true;
            }
            // handle defect value filtering
            if (item.result_type.kind === 'Defect' && key === 'value') {
              if (
                vrsFilter[key].includes(
                  `severity-${item.result_type.type_attributes[key]}`
                )
              )
                return true;
              return false;
            }
            // handle general filtering
            if (
              vrsFilter[key].includes(
                `${item.result_type.type_attributes[key]}`
              )
            ) {
              return true;
            }
          })
        : results;
      if (index > 0 && index < filterKeys.length) {
        results = intersectionBy(
          sorted[key],
          sorted[filterKeys[index - 1]],
          'id'
        );
      } else {
        results = sorted[key];
      }
    });
    this.setState({ results });
  }

  handleVrsFilterClick = (type) => {
    const { vrsFilter } = this.state;
    const { defaultOptions /* , level */ } = this.props;
    const defaultType = type;
    // if (['rroe', 'tc'].includes(type)) defaultType = 'risk';
    // else if (type === 'size' && level > 1) defaultType = 't_shirt_size';
    // else defaultType = type;

    const defaults = defaultOptions[`${defaultType}_defaults`];
    const fieldDefaults = defaults ? defaults.values : {};
    const severityOptions = defaultOptions.severity_defaults
      ? defaultOptions.severity_defaults.values
      : {};
    const severityFilters = Object.keys(severityOptions).map(
      (s) => `severity-${s}`
    );
    const allOptions =
      type === 'value'
        ? Object.keys(fieldDefaults).concat(severityFilters)
        : Object.keys(fieldDefaults);
    if (
      vrsFilter[type] &&
      vrsFilter[type].length === Object.keys(allOptions).length + 1
    ) {
      vrsFilter[type] = [];
      this.setState({ vrsFilter });
    }
  };

  buildFilterBySprint = (sprint) => {
    const { sprintFilter } = this.state;
    const { cadences } = this.props;
    let newSprintFilter = JSON.parse(JSON.stringify(sprintFilter));

    if (sprint.target && sprint.target.type === 'button') {
      if (newSprintFilter.length === cadences.length + 1) {
        newSprintFilter = [];
      } else {
        newSprintFilter = cadences.map((sp) => sp.id);
        newSprintFilter.push('unassigned');
      }
    } else if (sprintFilter.includes(sprint)) {
      newSprintFilter = sprintFilter.filter((id) => id !== sprint);
    } else {
      newSprintFilter.push(sprint);
    }

    this.setState({ sprintFilter: newSprintFilter }, () => {
      if (!this.props.noPreserve) this.saveComponentState();
    });
  };

  filterBySprint = (result) => {
    const selectedSprints = this.state.sprintFilter;
    let includes = true;
    if (selectedSprints.length > 0) {
      const currentCadenceId = result.current_cadence
        ? result.current_cadence.id
        : result.current_cadence_id;
      includes =
        currentCadenceId &&
        selectedSprints.some((selected) => currentCadenceId === selected);
      if (
        selectedSprints.includes('unassigned') &&
        !result.cadence_ids.length
      ) {
        includes = true;
      }
    }
    return includes;
  };

  buildFilterByState = (event, stateId) => {
    const { stateFilter } = this.state;
    const stateOptions = WorkPackageStatuses;
    let newStateFilter = JSON.parse(JSON.stringify(stateFilter));
    if (event.target && event.target.type === 'button') {
      if (stateFilter.length === Object.keys(stateOptions).length) {
        newStateFilter = [];
      } else {
        newStateFilter = Object.keys(stateOptions).map(
          (option) => stateOptions[option]
        );
      }
    } else if (stateFilter.indexOf(stateId) !== -1) {
      newStateFilter = stateFilter.filter((id) => id !== stateId);
    } else {
      newStateFilter.push(stateId);
    }
    this.setState({ stateFilter: newStateFilter }, () => {
      if (!this.props.noPreserve) this.saveComponentState();
    });
  };

  filterByState = (result) => {
    const { stateFilter } = this.state;
    if (stateFilter.length > 0) {
      return stateFilter.includes(result.state);
    }
    return true;
  };

  selectColumns(selected, checked) {
    const { tableFields } = this.state;
    const field = tableFields.find(
      (tableField) => tableField.name === selected
    );
    if (checked) {
      field.visible = true;
    } else {
      field.visible = false;
    }
    this.setState({ tableFields }, () => {
      if (!this.props.noPreserve) this.saveComponentState();
    });
  }

  applyFilters(results) {
    const { showTrash } = this.props;
    let filteredResults = results;
    if (!showTrash) {
      filteredResults = results.filter((result) => !result.deleted_at);
    }
    return filteredResults
      .filter(this.filterBySprint)
      .filter(this.filterByState);
  }

  headerFilter(fieldName, field) {
    const {
      vrsFilter,
      sortField,
      sortDescending,
      sprintFilter,
      stateFilter
    } = this.state;

    let filterClass;
    const defaults = this.props.defaultOptions[`${fieldName}_defaults`];
    const fieldDefaults = defaults ? defaults.values : {};

    filterClass =
      vrsFilter[fieldName] &&
      vrsFilter[fieldName].length > 0 &&
      vrsFilter[fieldName].length !== Object.keys(fieldDefaults).length + 1;

    if (fieldName === 'value') {
      const severityOptions = this.props.defaultOptions.severity_defaults
        ? this.props.defaultOptions.severity_defaults.values
        : {};

      filterClass =
        vrsFilter[fieldName] &&
        vrsFilter[fieldName].length > 0 &&
        vrsFilter[fieldName].length !==
          Object.keys(fieldDefaults).length +
            Object.keys(severityOptions).length +
            1;

      return (
        <div className={`header-filter ${fieldName}`}>
          {filterClass && <div className={`filter-icon ${fieldName}`} />}
          <div className="filter-group">
            <ValueFilterDropdown
              title={field.name}
              valueOptions={fieldDefaults}
              severityOptions={severityOptions}
              buildFilter={this.buildVrsFilter}
              checkedOptions={vrsFilter[fieldName] || []}
              onFilterClick={this.handleVrsFilterClick}
            />
          </div>
        </div>
      );
    }

    if (['risk', 'size'].includes(fieldName)) {
      return (
        <div className={`header-filter ${fieldName}`}>
          {filterClass && <div className={`filter-icon ${fieldName}`} />}
          <div className="filter-group">
            <VrsFilterDropdown
              title={field.name}
              options={fieldDefaults}
              buildFilter={this.buildVrsFilter}
              checkedOptions={vrsFilter[fieldName] || []}
              onFilterClick={this.handleVrsFilterClick}
            />
          </div>
        </div>
      );
    }
    if (fieldName === 'sprint') {
      return (
        <div className={`header-filter ${fieldName}`}>
          <div className="filter-group">
            <SprintFilter
              type={fieldName}
              options={this.props.cadences}
              filter={this.buildFilterBySprint}
              checkedOptions={sprintFilter}
            />
          </div>
        </div>
      );
    }
    if (fieldName === 'state') {
      filterClass =
        stateFilter &&
        stateFilter.length > 0 &&
        stateFilter.length !== Object.keys(WorkPackageStatuses).length;
      return (
        <div className={`header-filter ${fieldName}`}>
          {filterClass && <div className={`filter-icon ${fieldName}`} />}
          <div className="filter-group">
            <StateFilter
              level={this.props.level}
              filter={this.buildFilterByState}
              options={WorkPackageStatuses}
              checkedOptions={stateFilter}
            />
          </div>
        </div>
      );
    }
    const selected = this.state.sortField === field.source;
    return (
      <div
        type="button"
        onClick={() => this.activateSort(field.source)}
        className={`table-header ${selected ? 'selected-sort' : ''}`}
      >
        {field.name}
      </div>
    );
  }

  calculateListDimensions() {
    const { listHeight, listWidth } = this.state;
    const modalBody = document.getElementById('plan');
    if (modalBody) {
      const updatedListWidth = this.listHeader.offsetWidth;
      const { bottom: listBottom } = modalBody.getBoundingClientRect();
      const { top: listTop } = this.listHeader.getBoundingClientRect();
      const updatedListHeight = listBottom - listTop - 25;
      if (listHeight !== updatedListHeight || listWidth !== updatedListWidth) {
        this.setState({
          listHeight: updatedListHeight,
          listWidth: updatedListWidth
        });
      }
    }
  }

  render() {
    const {
      expandedItems,
      isWorkPackageFetching,
      isDragging,
      resultIds,
      cadence,
      scopeTab
    } = this.props;
    const { tableFields, listHeight, listWidth } = this.state;
    const visibleFields = tableFields.filter((field) => field.visible);

    const results = this.applyFilters(this.state.results);
    return (
      <div className="table">
        {!this.props.noPreserve && (
          <div className="add-columns row">
            <div className="col-md-3">
              <div className="btn-group">
                <button
                  type="button"
                  className="btn add-story dropdown-toggle"
                  data-toggle="dropdown"
                  aria-haspopup="true"
                  aria-expanded="false"
                >
                  Add Columns <span className="caret" />
                </button>
                <AddColumn
                  fields={tableFields}
                  handleChange={this.selectColumns}
                />
              </div>
            </div>
            <div className="col-md-offset-2 col-md-1">
              <Holon action="/planwall/r/2" />
            </div>
          </div>
        )}
        <div
          ref={(e) => {
            this.listHeader = e;
          }}
          className="title-container"
        >
          <div className="row">
            <div className="story-row-title col-md-12">
              <div
                ref={(e) => {
                  this.leftFielGroup = e;
                }}
                className="left-field-group"
              >
                {visibleFields
                  .filter((field) => field.groupLeft)
                  .map((field) => {
                    const fieldName = field.name.toLowerCase();
                    return (
                      <div
                        key={field.name}
                        className={`${fieldName}-col ${fieldName}-col-header results-table-header`}
                        ref={(e) => {
                          this.state.fieldWidths[fieldName] = e
                            ? e.offsetWidth
                            : 0;
                          return e;
                        }}
                      >
                        {this.headerFilter(fieldName, field)}
                      </div>
                    );
                  })}
              </div>
              <div
                ref={(e) => {
                  this.rightFielGroup = e;
                }}
                className="right-field-group"
                style={{ paddingRight: '5px' }}
              >
                {visibleFields
                  .filter((field) => !field.groupLeft)
                  .map((field) => {
                    const fieldName = field.name.toLowerCase();
                    return (
                      <div
                        key={field.name}
                        className={`${fieldName}-col ${fieldName}-col-header`}
                        ref={(e) => {
                          this.state.fieldWidths[fieldName] = e
                            ? e.offsetWidth
                            : 0;
                          return e;
                        }}
                      >
                        {this.headerFilter(fieldName, field)}
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
          <StoryList
            results={results}
            source="ResultsTable"
            fields={visibleFields}
            dropDown={this.props.dropDown}
            setDropdown={this.props.setDropdown}
            listHeight={listHeight}
            listWidth={listWidth}
            fieldWidths={this.state.fieldWidths}
            expandedItems={expandedItems}
            isWorkPackageFetching={isWorkPackageFetching}
            isDragging={isDragging}
            resultIds={resultIds}
            cadence={cadence}
            virtualized
            allResultsTogether
            expand
            scopeTab={scopeTab}
          />
        </div>
      </div>
    );
  }
}

ResultsTable.propTypes = {
  resultList: PropTypes.array,
  cadences: PropTypes.array,
  defaultOptions: PropTypes.object,
  preservedState: PropTypes.object,
  dropDown: PropTypes.object,
  noPreserve: PropTypes.bool,
  preserveComponentState: PropTypes.func,
  sortList: PropTypes.func,
  setDropdown: PropTypes.func
};

const mapStateToProps = (state, ownProps) => {
  let resultList;
  if (ownProps.placement === 'CadencePlanTab') {
    resultList = filterOutAcceptedStories(state, ownProps);
  } else {
    resultList = ownProps.resultList || state.workPackageReducer.stories;
  }

  return {
    resultList,
    defaultOptions: state.defaultOptionsReducer.defaultOptions,
    cadences: state.cadenceReducer.cadences,
    isWorkPackageFetching:
      state.workPackageReducer.isSending || state.workPackageReducer.isFetching,
    isDragging: state.notificationReducer.dragInProgress,
    expandedItems: state.listReducer.expandedItems,
    showTrash: state.trashReducer.showTrash
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      sortList
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(ResultsTable);
